<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  export type AdminSection = 'dashboard' | 'events' | 'guestlist' | 'people' | 'orders' | 'settings';

  export let activeSection: AdminSection = 'events';
  export let busy = false;

  const dispatch = createEventDispatcher();

  const sections: Array<{ id: AdminSection; label: string; icon: string }> = [
    { id: 'dashboard', label: 'Overview', icon: '📊' },
    { id: 'events', label: 'Events', icon: '🪩' },
    { id: 'guestlist', label: 'Guestlist', icon: '🎟️' },
    { id: 'people', label: 'People', icon: '🧑‍🤝‍🧑' },
    { id: 'orders', label: 'Orders & Extras', icon: '💳' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  function handleNavigate(section: AdminSection) {
    dispatch('navigate', { section });
  }
</script>

<aside class="admin-sidebar">
  <header class="sidebar-header">
    <span class="sidebar-badge">Buckshot Ops</span>
    <h1>Admin Console</h1>
    {#if busy}
      <span class="sync-status">Syncing…</span>
    {/if}
  </header>

  <nav class="sidebar-nav">
    {#each sections as section}
      <button
        type="button"
        class:active={section.id === activeSection}
        on:click={() => handleNavigate(section.id)}
      >
        <span class="icon">{section.icon}</span>
        <span>{section.label}</span>
      </button>
    {/each}
  </nav>
</aside>

<style>
  .admin-sidebar {
    width: 240px;
    padding: var(--space-5) var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    border-right: 1px solid rgba(255, 255, 255, 0.06);
    background: linear-gradient(180deg, rgba(14, 16, 24, 0.96), rgba(14, 16, 24, 0.88));
    backdrop-filter: blur(20px);
  }

  .sidebar-header {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .sidebar-header h1 {
    font-size: 1.15rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--color-text-primary);
  }

  .sidebar-badge {
    display: inline-flex;
    align-self: flex-start;
    padding: 0.25rem 0.85rem;
    border-radius: var(--radius-lg);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.08);
  }

  .sync-status {
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  .sidebar-nav {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .sidebar-nav button {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.65rem 0.9rem;
    border-radius: var(--radius-lg);
    background: transparent;
    color: var(--color-text-muted);
    font-size: 0.95rem;
    font-weight: 500;
    transition: background 150ms ease, color 150ms ease, transform 150ms ease;
    border: none;
    cursor: pointer;
  }

  .sidebar-nav button .icon {
    font-size: 1.1rem;
  }

  .sidebar-nav button:hover {
    color: var(--color-text-primary);
    background: rgba(255, 255, 255, 0.05);
    transform: translateX(2px);
  }

  .sidebar-nav button.active {
    color: var(--color-text-primary);
    background: linear-gradient(135deg, rgba(240, 169, 74, 0.24), rgba(178, 127, 244, 0.18));
    box-shadow: 0 0 0 1px rgba(240, 169, 74, 0.4);
  }

  @media (max-width: 1024px) {
    .admin-sidebar {
      flex-direction: row;
      width: 100%;
      border-right: none;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      align-items: center;
    }

    .sidebar-nav {
      flex-direction: row;
      flex-wrap: wrap;
    }

    .sidebar-nav button {
      flex: 1 0 140px;
      justify-content: center;
    }
  }
</style>
